/**
 * DataSources API Hooks
 *
 * React hooks for managing datasources in projects
 */

import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import type { DataSourceDefinition } from "@onecreator/shared";

// =============================================================================
// TYPES
// =============================================================================

interface AdapterInfo {
  id: string;
  name: string;
  description?: string;
  operations: string[];
}

interface TestResult {
  status: "ok" | "error";
  latency?: number;
  sampleData?: any;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook to list datasources for a project
 */
export function useDataSources(projectId: string | undefined) {
  const [dataSources, setDataSources] = useState<DataSourceDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDataSources = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get<{
        success: boolean;
        data: DataSourceDefinition[];
      }>(`/projects/${projectId}/datasources`);

      if (response.data.success) {
        setDataSources(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load datasources");
      console.error("[useDataSources] Error:", err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadDataSources();
  }, [loadDataSources]);

  return {
    dataSources,
    loading,
    error,
    reload: loadDataSources,
  };
}

/**
 * Hook to get available adapters
 */
export function useAvailableAdapters() {
  const [adapters, setAdapters] = useState<AdapterInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAdapters = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get<{
          success: boolean;
          data: AdapterInfo[];
        }>("/datasource-adapters");

        if (response.data.success) {
          setAdapters(response.data.data);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to load adapters");
        console.error("[useAvailableAdapters] Error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadAdapters();
  }, []);

  return {
    adapters,
    loading,
    error,
  };
}

/**
 * Hook to manage datasource CRUD operations
 */
export function useDataSourceMutations(projectId: string | undefined) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Create a new datasource
   */
  const createDataSource = useCallback(
    async (dataSource: DataSourceDefinition) => {
      if (!projectId) throw new Error("Project ID is required");

      setLoading(true);
      setError(null);

      try {
        const response = await api.post(
          `/projects/${projectId}/datasources`,
          dataSource,
        );

        if (!response.data.success) {
          throw new Error(
            response.data.message || "Failed to create datasource",
          );
        }

        return response.data.data;
      } catch (err: any) {
        const errorMsg =
          err.response?.data?.message || "Failed to create datasource";
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [projectId],
  );

  /**
   * Update existing datasource
   */
  const updateDataSource = useCallback(
    async (dataSource: DataSourceDefinition) => {
      if (!projectId) throw new Error("Project ID is required");

      setLoading(true);
      setError(null);

      try {
        const response = await api.put(
          `/projects/${projectId}/datasources/${dataSource.id}`,
          dataSource,
        );

        if (!response.data.success) {
          throw new Error(
            response.data.message || "Failed to update datasource",
          );
        }

        return response.data.data;
      } catch (err: any) {
        const errorMsg =
          err.response?.data?.message || "Failed to update datasource";
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [projectId],
  );

  /**
   * Delete datasource
   */
  const deleteDataSource = useCallback(
    async (dataSourceId: string) => {
      if (!projectId) throw new Error("Project ID is required");

      setLoading(true);
      setError(null);

      try {
        const response = await api.delete(
          `/projects/${projectId}/datasources/${dataSourceId}`,
        );

        if (!response.data.success) {
          throw new Error(
            response.data.message || "Failed to delete datasource",
          );
        }

        return true;
      } catch (err: any) {
        const errorMsg =
          err.response?.data?.message || "Failed to delete datasource";
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [projectId],
  );

  /**
   * Test datasource connection
   */
  const testDataSource = useCallback(
    async (
      dataSourceId: string,
      operation = "suggest",
      params = {},
    ): Promise<TestResult> => {
      if (!projectId) throw new Error("Project ID is required");

      setLoading(true);
      setError(null);

      try {
        const response = await api.post(
          `/projects/${projectId}/datasources/${dataSourceId}/test`,
          { operation, params },
        );

        if (response.data.success) {
          return response.data.data as TestResult;
        } else {
          return {
            status: "error",
            error: response.data.error,
          };
        }
      } catch (err: any) {
        const errorMsg =
          err.response?.data?.message || "Connection test failed";
        setError(errorMsg);
        return {
          status: "error",
          error: {
            code: "TEST_FAILED",
            message: errorMsg,
          },
        };
      } finally {
        setLoading(false);
      }
    },
    [projectId],
  );

  return {
    createDataSource,
    updateDataSource,
    deleteDataSource,
    testDataSource,
    loading,
    error,
  };
}
